import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { SlideUpModal } from "./SlideUpModal/SlideUpModal";
import { useAuth, UserContext } from "src/context";
import { useGetBorrowerData } from "src/hooks/useGetBorrowerData";
import { Button, Logout01Icon, MoreVerticalIcon, Spinner } from "@porchpass/ui";

export function AccountMenu() {
  const { dealerName } = useParams();
  const [isOpen, setIsOpen] = useState(false);
  const { userData } = useContext(UserContext);
  const { user: firebaseUser } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const isPublicAuthRoute = /\/login(\/|$)|\/email-sent|\/sms-sent/.test(
    pathname,
  );
  const isReturningUser = searchParams.get("isReturningUser");
  const { borrowerData, isLoading, execute } = useGetBorrowerData({
    dealerId: userData?.dealer_id ?? "",
    borrowerId: userData?.borrower_id ?? "",
  });

  const applicationId = userData?.application_id;
  const status = userData?.status;
  const hasRegisterUrl = window.location.pathname.includes("/register");
  const hasProgressUrl = window.location.pathname.includes("/progress");
  const hasDashboardUrl = window.location.pathname.includes("/dashboard");

  useEffect(() => {
    if (isOpen) {
      void execute();
    }
  }, [isOpen]);

  if (!firebaseUser || isPublicAuthRoute) {
    return null;
  }

  function getBorrowerName() {
    if (!borrowerData?.first_name || !borrowerData?.last_name) {
      return "";
    }
    return `${borrowerData?.first_name} ${borrowerData?.last_name}`;
  }

  const handleApplicationDetailClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    setIsOpen(false);
    navigate(`/${dealerName}/application/${applicationId}/progress`);
  };

  const handleDashboardClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsOpen(false);
    navigate(`/${dealerName}/dashboard`);
  };
  const showProgressPageLink =
    applicationId &&
    status !== "INVITED" &&
    !hasProgressUrl &&
    !hasDashboardUrl;
  const showDashboardLink =
    (!hasRegisterUrl || isReturningUser) && !hasDashboardUrl;

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => setIsOpen(true)}
        aria-label="Account Navigation Menu"
      >
        <MoreVerticalIcon size={20} />
      </Button>
      <SlideUpModal
        title={isLoading ? <Spinner size="s" /> : getBorrowerName()}
        isOpen={isOpen}
        closeHandler={() => setIsOpen(false)}
        hasDismissButton={false}
        dismissible
        showCloseButton
        actionButtonText={
          <span className="flex items-center justify-center gap-3">
            <Logout01Icon size={20} />
            Logout
          </span>
        }
        actionButtonClickHandler={() => navigate(`/${dealerName}/logout`)}
        variant="secondary"
      >
        <div className="flex flex-col gap-3">
          {showProgressPageLink && (
            <Button
              type="button"
              variant="secondary"
              size="l"
              className="w-full"
              onClick={handleApplicationDetailClick}
            >
              Review Current Application
            </Button>
          )}
          {showDashboardLink && (
            <Button
              type="button"
              variant="secondary"
              size="l"
              className="w-full"
              onClick={handleDashboardClick}
            >
              View Dashboard
            </Button>
          )}
        </div>
      </SlideUpModal>
    </>
  );
}
